import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Users } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { listUsers, setUserRole } from '@/api/endpoints/auth';
import { queryKeys } from '@/api/queryKeys';
import { errorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';
import { ROLES, type Role, type User } from '@/types/api';
import { titleCase } from '@/lib/format';

const ROLE_EFFECTS: Record<Role, string> = {
  vehicle_owner: 'Sees only their own vehicles, devices and alerts.',
  fleet_manager: 'Sees every vehicle and device, and receives SMS for major crashes.',
  emergency_responder: 'Sees every alert and receives SMS and a call for critical crashes.',
  administrator: 'Full access, including provisioning and record deletion.',
};

/**
 * Roles are granted, never self-assigned — signing up always produces a vehicle
 * owner, because an account that could name itself administrator could read the
 * whole fleet. Granting is audit-logged server-side with the acting administrator.
 */
export function RolesPage() {
  const { user: me } = useAuth();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{ user: User; role: Role } | null>(null);

  const users = useQuery({ queryKey: queryKeys.users, queryFn: () => listUsers() });

  const mutation = useMutation({
    mutationFn: ({ user, role }: { user: User; role: Role }) => setUserRole(user.id, role),
    onSuccess: async (updated) => {
      setPending(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.users });
      toast.success(`${updated.name} is now a ${titleCase(updated.role).toLowerCase()}.`);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const rows = users.data ?? [];

  return (
    <>
      <PageHeader
        title="Roles"
        description="Grant a role to an existing user. Changes take effect the next time they sign in."
      />

      <Card>
        <CardHeader title="Users" description={rows.length > 0 ? `${rows.length} accounts.` : undefined} />

        {users.isPending ? (
          <SkeletonRows rows={5} />
        ) : users.isError ? (
          <ErrorState error={users.error} onRetry={users.refetch} />
        ) : rows.length === 0 ? (
          <EmptyState icon={Users} title="No users" description="Nobody has registered yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="border-b border-line text-left text-ink-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="hidden px-4 py-2 font-medium sm:table-cell">Email</th>
                  <th className="hidden px-4 py-2 font-medium md:table-cell">Emergency contact</th>
                  <th className="px-4 py-2 font-medium">Role</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((user) => (
                  <tr key={user.id} className="transition-colors hover:bg-surface-sunken">
                    <td className="px-4 py-2.5">
                      {user.name}
                      {me?.id === user.id && <span className="ml-2 text-ink-muted">(you)</span>}
                    </td>
                    <td className="hidden px-4 py-2.5 text-ink-secondary sm:table-cell">{user.email}</td>
                    <td className="hidden px-4 py-2.5 text-ink-secondary md:table-cell">
                      {user.emergency_contact_name ?? <span className="text-ink-muted">Not set</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <select
                        value={user.role}
                        aria-label={`Role for ${user.name}`}
                        // Demoting yourself locks you out of this page, and there may
                        // be no other administrator to undo it.
                        disabled={me?.id === user.id}
                        onChange={(event) =>
                          setPending({ user, role: event.target.value as Role })
                        }
                        className="h-8 rounded-[--radius-control] border border-line bg-surface px-2 text-[13px] outline-none focus:border-accent focus:ring-2 focus:ring-accent-ring/40 disabled:cursor-not-allowed disabled:text-ink-muted"
                      >
                        {ROLES.map((option) => (
                          <option key={option} value={option}>
                            {titleCase(option)}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="mt-4 max-w-lg">
        <CardHeader title="What each role can do" />
        <CardBody>
          <dl className="space-y-3 text-[13px]">
            {ROLES.map((option) => (
              <div key={option}>
                <dt className="font-medium text-ink">{titleCase(option)}</dt>
                <dd className="text-ink-secondary">{ROLE_EFFECTS[option]}</dd>
              </div>
            ))}
          </dl>
        </CardBody>
      </Card>

      {pending && (
        <Dialog
          open
          onOpenChange={(open) => !open && setPending(null)}
          title={`Make ${pending.user.name} a ${titleCase(pending.role).toLowerCase()}?`}
          footer={
            <>
              <Button variant="ghost" onClick={() => setPending(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={mutation.isPending}
                onClick={() => mutation.mutate(pending)}
              >
                Grant role
              </Button>
            </>
          }
        >
          <div className="space-y-2 text-[13px] text-ink-secondary">
            <p>{ROLE_EFFECTS[pending.role]}</p>
            <p>
              They are currently a {titleCase(pending.user.role).toLowerCase()}. The change applies
              the next time they sign in.
            </p>
          </div>
        </Dialog>
      )}
    </>
  );
}
